import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PharmHeader } from '../pharm-header/pharm-header';
import { PharmSidebar } from '../pharm-sidebar/pharm-sidebar';

const baseUrl = 'https://logs.proofile.com.ng/api';

interface DrugIssue {
  store_id: number | null;
  stock_item_id: number | null;
  quantity: number | null;
  patient_type: 'in-patient' | 'out-patient';
  patient_id: number | null;
  note: string;
}

@Component({
  selector: 'app-pharmacy-dispensary',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, DatePipe, PharmHeader, PharmSidebar],
  templateUrl: './pharmacy-dispensary.html',
  styleUrl: './pharmacy-dispensary.css'
})
export class PharmacyDispensary implements OnInit {
  issue: DrugIssue = {
    store_id: null,
    stock_item_id: null,
    quantity: null,
    patient_type: 'out-patient',
    patient_id: null,
    note: ''
  };

  stores: any[] = []; 
  selectedStoreName = '';
  selectedDrugName: string = '';
  storeId!: number;
  stockItems: any[] = [];
  patients: any[] = [];
  filteredPatients: any[] = [];
  recentIssues: any[] = [];
  searchPatient: string = '';
  stockBalance: number | null = null;

  showIssueModal = false;
  isSubmitting = false;
  loadingIssues = true;
  successMessage = '';
  errorMessage = '';
  currentPage = 1;
  pageSize = 10;

  filters = {
    drug: '',
    patient: '',
    type: '',
    store: '',
    quantity: null as number | null,
    date: ''
  };

  constructor(private http: HttpClient) {}

  ngOnInit() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    this.storeId = user?.store_id;

    if (!this.storeId) {
      console.error('❌ No store ID found');
      return;
    }

    this.issue.store_id = this.storeId;

    this.http.get<any>(`${baseUrl}/stores/${this.storeId}`).subscribe(store => {
      this.stores = [store];
      this.selectedStoreName = store.name;
    });

    this.loadStockItems();
    this.loadPatients();
    this.loadRecentIssues();
  }

  loadStockItems() {
    this.http.get<any[]>(`${baseUrl}/store-stock/${this.storeId}`).subscribe(data => {
      this.stockItems = data;
    });
  }

  loadPatients() {
    this.http.get<any[]>(`${baseUrl}/patients`).subscribe(data => {
      this.patients = data;
      this.filteredPatients = [...data];
    });
  }

  loadRecentIssues() {
    this.loadingIssues = true;
    this.http.get<any[]>(`${baseUrl}/issues/by-store/${this.storeId}`).subscribe(data => {
      this.recentIssues = data;
      this.loadingIssues = false;
    });
  }

  checkStockBalance() {
    if (this.issue.stock_item_id && this.issue.store_id) {
      const item = this.stockItems.find(i =>
        i.stock_item_id === this.issue.stock_item_id || i.id === this.issue.stock_item_id
      );
      this.stockBalance = item?.quantity || 0;
      this.selectedDrugName = item?.drug_name || '';
    } else {
      this.stockBalance = null;
      this.selectedDrugName = '';
    }
  }

  filterPatients() {
    const term = this.searchPatient.toLowerCase();
    this.filteredPatients = this.patients.filter(p =>
      `${p.surname} ${p.other_names}`.toLowerCase().includes(term)
    );
  }

  submitIssue() {
    this.isSubmitting = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.http.post(`${baseUrl}/product-issues`, this.issue).subscribe({
      next: () => {
        this.successMessage = '✅ Drug issued successfully!';
        this.loadRecentIssues();
        this.loadStockItems();
        this.showIssueModal = false;
      },
      error: () => {
        this.errorMessage = '❌ Failed to issue drug';
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }

  filteredIssues() {
    return this.recentIssues.filter(issue => {
      const matchesDrug = this.filters.drug
        ? issue.stock_item_name.toLowerCase().includes(this.filters.drug.toLowerCase())
        : true;

      const matchesPatient = this.filters.patient
        ? issue.patient_name.toLowerCase().includes(this.filters.patient.toLowerCase())
        : true;

      const matchesType = this.filters.type
        ? issue.patient_type === this.filters.type
        : true;

      const matchesStore = this.filters.store
        ? issue.store_name.toLowerCase().includes(this.filters.store.toLowerCase())
        : true;

      const matchesQuantity = this.filters.quantity !== null
        ? issue.quantity === this.filters.quantity
        : true;

      const matchesDate = this.filters.date
        ? new Date(issue.created_at).toISOString().slice(0, 10) === this.filters.date
        : true;

      return matchesDrug && matchesPatient && matchesType && matchesStore && matchesQuantity && matchesDate;
    });
  }

  paginatedIssues() {
    const filtered = this.filteredIssues();
    const start = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(start, start + this.pageSize);
  }
}
